import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const FORMAS_PAGO = [
    { label: 'Efectivo',             value: 1 },
    { label: 'Transferencia',        value: 2 },
    { label: 'Cheque',               value: 3 },
    { label: 'Débito automático',    value: 4 },
];

const BANCOS = [
    { label: 'Galicia',      value: 'Galicia' },
    { label: 'Hipotecario',  value: 'Hipotecario' },
    { label: 'Macro',        value: 'Macro' },
    { label: 'MercadoPago',  value: 'MercadoPago' },
    { label: 'Santander',    value: 'Santander' },
];

// Cada banco exporta su extracto en un formato distinto (no todos son .xlsx).
const EXTENSIONES_POR_BANCO = {
    Galicia:     ['xlsx'],
    Hipotecario: ['csv'],
    Macro:       ['xls'],
    MercadoPago: ['xlsx'],
    Santander:   ['xls'],
};

const CONCEPTOS = [
    { label: 'Expensa',       value: 1 },
    { label: 'Plan de pago',  value: 2 },
];

const PAGO_EMPTY = {
    unidadFuncionalId: null,
    fechaPago:         null,
    importe:           null,
    formaPagoId:       null,
    detalle:           '',
    periodo:           null,
    conceptoPagoId:    1,
    planPagoCuotaId:   null,
    esImportado:       false,
};

const fmtFecha  = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtImporte = (v) => v != null ? `$ ${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '—';
const fmtPeriodo = (v) => v
    ? new Date(v).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
    : '—';
const fmtSize = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;

const FormularioPago = () => {
    const toast = useRef(null);
    const fileInputRef = useRef(null);
    const comprobanteInputRef = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const t = useTerminologia();

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');
    const usuarioId = JSON.parse(localStorage.getItem('user') || '{}').id
        || JSON.parse(localStorage.getItem('user') || '{}').usuarioId
        || 0;

    // ─── Data ────────────────────────────────────────────────────────────────────
    const [pagos,  setPagos]  = useState([]);
    const [ufs,    setUfs]    = useState([]);
    const [loading, setLoading] = useState(false);

    // ─── Planes de pago y cuotas (para los combos del modal) ──────────────────────
    const [planes, setPlanes] = useState([]);
    const [loadingPlanes, setLoadingPlanes] = useState(false);
    const [planPagoId, setPlanPagoId] = useState(null);
    const [cuotas, setCuotas] = useState([]);
    const [loadingCuotas, setLoadingCuotas] = useState(false);

    // ─── Filtros ─────────────────────────────────────────────────────────────────
    const [filtroUF,      setFiltroUF]      = useState(null);
    const [filtroPeriodo, setFiltroPeriodo] = useState(null);

    // ─── Modal registro manual ────────────────────────────────────────────────────
    const [modalPago,  setModalPago]  = useState(false);
    const [form,       setForm]       = useState({ ...PAGO_EMPTY });
    const [submitting, setSubmitting] = useState(false);
    const [archivo,    setArchivo]    = useState(null);
    const [dragOver,   setDragOver]   = useState(false);

    // ─── Expensa: vencimientos (GetInformarPagosBarrio) ───────────────────────────
    const [infoExpensa,        setInfoExpensa]       = useState(null);
    const [loadingInfoExpensa, setLoadingInfoExpensa] = useState(false);
    const [ventanaPago,        setVentanaPago]       = useState(null); // 'v1' | 'v2' | 'otros'

    // ─── Modal importar Excel ─────────────────────────────────────────────────────
    const [modalExcel,    setModalExcel]    = useState(false);
    const [excelFile,     setExcelFile]     = useState(null);
    const [excelPeriodo,  setExcelPeriodo]  = useState(null);
    const [excelBanco,    setExcelBanco]    = useState(null);
    const [importing,     setImporting]     = useState(false);

    // ─── Pendientes de imputar (movimientos importados sin UF resuelta) ───────────
    const [pendientes,        setPendientes]        = useState([]);
    const [loadingPendientes, setLoadingPendientes]  = useState(false);
    const [pendientesVisible, setPendientesVisible]  = useState(false);
    const [ufPorPendiente,    setUfPorPendiente]     = useState({});
    const [resolviendoId,     setResolviendoId]      = useState(null);

    // ─── Carga UFs ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data))
            .catch(() => {});
    }, [barrio?.id]);

    // ─── Carga planes de pago de la UF seleccionada ───────────────────────────────
    useEffect(() => {
        setPlanPagoId(null);
        setCuotas([]);
        if (!modalPago || form.conceptoPagoId !== 2 || !form.unidadFuncionalId) {
            setPlanes([]);
            return;
        }
        setLoadingPlanes(true);
        axios.get(`${HOST_API}api/PlanPago/GetPlanesPago`, {
            params: { administracionId, unidadFuncionalId: form.unidadFuncionalId },
        })
            .then(({ data }) => setPlanes((data || []).filter(p => p.saldo > 0)))
            .catch(() => setPlanes([]))
            .finally(() => setLoadingPlanes(false));
    }, [modalPago, form.conceptoPagoId, form.unidadFuncionalId]);

    // ─── Carga cuotas del plan de pago seleccionado ────────────────────────────────
    useEffect(() => {
        if (!planPagoId) {
            setCuotas([]);
            return;
        }
        setLoadingCuotas(true);
        axios.get(`${HOST_API}api/PlanPago/GetPlanPagoCuotas`, {
            params: { administracionId, planPagoId },
        })
            .then(({ data }) => setCuotas((data || []).filter(c => c.saldo > 0)))
            .catch(() => setCuotas([]))
            .finally(() => setLoadingCuotas(false));
    }, [planPagoId]);

    // ─── Carga info de vencimientos de la expensa (Concepto = Expensa) ───────────
    useEffect(() => {
        setInfoExpensa(null);
        setVentanaPago(null);
        setForm(f => ({ ...f, importe: null }));
        if (!modalPago || form.conceptoPagoId !== 1 || !form.unidadFuncionalId || !form.periodo || !barrio?.id) return;
        setLoadingInfoExpensa(true);
        axios.get(`${HOST_API}api/pagosUF/GetInformarPagosBarrio`, {
            params: {
                unidadFuncionalId: form.unidadFuncionalId,
                barrioId:          barrio.id,
                administracionId,
                conceptoPagoId:    1,
                periodo:           form.periodo.toISOString(),
            },
        })
            .then(({ data }) => setInfoExpensa(data))
            .catch(() => setInfoExpensa(null))
            .finally(() => setLoadingInfoExpensa(false));
    }, [modalPago, form.conceptoPagoId, form.unidadFuncionalId, form.periodo, barrio?.id]);

    const seleccionarVencimiento = (tipo) => {
        setVentanaPago(tipo);
        if (tipo === 'v1') {
            setForm(f => ({ ...f, importe: infoExpensa?.importePrimerVencimiento ?? null }));
        } else if (tipo === 'v2') {
            setForm(f => ({ ...f, importe: infoExpensa?.importeSegundoVencimiento ?? null }));
        } else {
            setForm(f => ({ ...f, importe: null }));
        }
    };

    // ─── Archivo (comprobante) ─────────────────────────────────────────────────────
    const setFile = (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.current.show({ severity: 'warn', summary: 'Archivo muy grande', detail: 'El archivo no puede superar 10 MB', life: 3500 });
            return;
        }
        setArchivo(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        setFile(e.dataTransfer.files[0]);
    };

    const handleRemoveFile = () => {
        setArchivo(null);
        if (comprobanteInputRef.current) comprobanteInputRef.current.value = '';
    };

    const planLabel = (p) =>
        `${p.concepto} · ${p.cantidadCuotas} cuotas · saldo $ ${Number(p.saldo).toLocaleString('es-AR')} · ${p.estadoPlanPago}`;

    const cuotaLabel = (c) =>
        `Cuota ${c.numeroCuota}/${c.cantidadCuotas} · $ ${Number(c.importeTotal).toLocaleString('es-AR')} (saldo $ ${Number(c.saldo).toLocaleString('es-AR')})`;

    const seleccionarPlan = (id) => {
        setPlanPagoId(id);
        setForm(f => ({ ...f, planPagoCuotaId: null }));
    };

    const seleccionarCuota = (planPagoCuotaId) => {
        const cuota = cuotas.find(c => c.planPagoCuotaId === planPagoCuotaId);
        setForm(f => ({
            ...f,
            planPagoCuotaId,
            importe: cuota ? cuota.saldo : f.importe,
            periodo: cuota ? new Date(cuota.periodo) : f.periodo,
        }));
    };

    // ─── Carga pagos ─────────────────────────────────────────────────────────────
    const fetchPagos = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            let url = `${HOST_API}api/pagosUF/GetPagos?administracionId=${administracionId}&barrioId=${barrio.id}`;
            if (filtroUF)      url += `&unidadFuncionalId=${filtroUF}`;
            if (filtroPeriodo) url += `&periodo=${filtroPeriodo.toISOString()}`;
            const { data } = await axios.get(url);
            setPagos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los pagos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, filtroUF, filtroPeriodo]);

    useEffect(() => { fetchPagos(); }, [fetchPagos]);

    // ─── Carga pendientes de imputar ───────────────────────────────────────────────
    const fetchPendientes = useCallback(async () => {
        if (!barrio?.id) return;
        setLoadingPendientes(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/pagosUF/GetPendientesImportacion`, {
                params: { barrioId: barrio.id, administracionId },
            });
            setPendientes(data ?? []);
        } catch {
            // silencioso: no tapamos la pantalla principal de pagos por esto
        } finally {
            setLoadingPendientes(false);
        }
    }, [barrio?.id]);

    useEffect(() => { fetchPendientes(); }, [fetchPendientes]);

    // ─── Registrar pago manual ────────────────────────────────────────────────────
    const guardarPago = async () => {
        if (!form.unidadFuncionalId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la unidad funcional', life: 3000 }); return; }
        if (!form.periodo)           { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el período', life: 3000 }); return; }
        if (!form.fechaPago)         { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese la fecha de pago', life: 3000 }); return; }
        if (!form.importe)           { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el importe', life: 3000 }); return; }
        if (!form.formaPagoId)       { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la forma de pago', life: 3000 }); return; }
        if (form.conceptoPagoId === 1 && !ventanaPago) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el vencimiento a abonar', life: 3000 }); return; }
        if (form.conceptoPagoId === 2 && !form.planPagoCuotaId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la cuota del plan de pago', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const modelo = {
                unidadFuncionalId: form.unidadFuncionalId,
                barrioId:          barrio.id,
                administracionId,
                fechaPago:         form.fechaPago,
                importe:           form.importe,
                formaPagoId:       form.formaPagoId,
                detalle:           form.detalle || null,
                usuarioId,
                periodo:           form.periodo,
                conceptoPagoId:    form.conceptoPagoId,
                planPagoCuotaId:   form.planPagoCuotaId || null,
                esImportado:       false,
                autoAprobar:       true,
            };

            const fd = new FormData();
            fd.append('model', JSON.stringify(modelo));
            if (archivo) fd.append('file', archivo);

            await axios.post(`${HOST_API}api/pagosUF/RegistrarPago`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Pago registrado correctamente', life: 3000 });
            setModalPago(false);
            setForm({ ...PAGO_EMPTY });
            setArchivo(null);
            if (comprobanteInputRef.current) comprobanteInputRef.current.value = '';
            fetchPagos();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo registrar el pago', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Aprobar / Rechazar pago pendiente ────────────────────────────────────────
    const aprobarPago = (pago) => {
        confirmDialog({
            message:      `¿Aprobar el pago de $ ${Number(pago.importe).toLocaleString('es-AR')} de UF ${pago.unidadFuncional}?`,
            header:       'Confirmar aprobación',
            icon:         'pi pi-check-circle',
            acceptLabel:  'Aprobar',
            rejectLabel:  'Cancelar',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/pagosUF/AprobarPago?pagoId=${pago.pagoId}&administracionId=${administracionId}`);
                    toast.current.show({ severity: 'success', summary: 'Aprobado', detail: 'Pago aprobado', life: 3000 });
                    fetchPagos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo aprobar el pago', life: 3000 });
                }
            },
        });
    };

    const rechazarPago = (pago) => {
        confirmDialog({
            message:      `¿Rechazar el pago de $ ${Number(pago.importe).toLocaleString('es-AR')} de UF ${pago.unidadFuncional}?`,
            header:       'Confirmar rechazo',
            icon:         'pi pi-times-circle',
            acceptLabel:  'Rechazar',
            rejectLabel:  'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/pagosUF/RechazarPago?pagoId=${pago.pagoId}&administracionId=${administracionId}`);
                    toast.current.show({ severity: 'success', summary: 'Rechazado', detail: 'Pago rechazado', life: 3000 });
                    fetchPagos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo rechazar el pago', life: 3000 });
                }
            },
        });
    };

    // ─── Anular pago ──────────────────────────────────────────────────────────────
    const anularPago = (pago) => {
        confirmDialog({
            message:      `¿Anular el pago de $ ${Number(pago.importe).toLocaleString('es-AR')} de UF ${pago.unidadFuncional}?`,
            header:       'Confirmar anulación',
            icon:         'pi pi-exclamation-triangle',
            acceptLabel:  'Anular',
            rejectLabel:  'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/pagosUF/AnularPagoUnidadFuncional?pagoId=${pago.pagoId}&administracionId=${administracionId}`);
                    toast.current.show({ severity: 'success', summary: 'Anulado', detail: 'Pago anulado', life: 3000 });
                    fetchPagos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo anular el pago', life: 3000 });
                }
            },
        });
    };

    // ─── Importar Excel ───────────────────────────────────────────────────────────
    const handleExcelChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        const extensionesValidas = excelBanco ? EXTENSIONES_POR_BANCO[excelBanco] : ['xlsx', 'xls', 'csv'];
        if (!extensionesValidas.includes(ext)) {
            toast.current.show({
                severity: 'warn', summary: 'Formato inválido',
                detail: `Para ${excelBanco || 'este banco'} el archivo debe ser .${extensionesValidas.join(' o .')}`,
                life: 3500,
            });
            return;
        }
        setExcelFile(f);
    };

    const importarExcel = async () => {
        if (!excelBanco)   { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el banco', life: 3000 }); return; }
        if (!excelFile)    { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione un archivo Excel', life: 3000 }); return; }
        if (!excelPeriodo) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el período', life: 3000 }); return; }

        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file',             excelFile);
            fd.append('barrioId',         barrio.id);
            fd.append('administracionId', administracionId);
            fd.append('usuarioId',        usuarioId);
            fd.append('periodo',          excelPeriodo.toISOString());
            fd.append('banco',            excelBanco);

            await axios.post(`${HOST_API}api/pagosUF/ImportarExcel`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.current.show({ severity: 'success', summary: 'Importación exitosa', detail: 'Los pagos fueron importados correctamente', life: 4000 });
            setModalExcel(false);
            setExcelFile(null);
            setExcelPeriodo(null);
            setExcelBanco(null);
            fetchPagos();
            fetchPendientes();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'Error al importar el archivo', life: 4000 });
        } finally {
            setImporting(false);
        }
    };

    // ─── Resolver un pendiente: asignarle UF o descartarlo ─────────────────────────
    const asignarUnidadFuncional = async (pendiente) => {
        const unidadFuncionalId = ufPorPendiente[pendiente.pagoImportadoPendienteId];
        if (!unidadFuncionalId) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: `Seleccione la ${t.UF} a imputar`, life: 3000 });
            return;
        }
        setResolviendoId(pendiente.pagoImportadoPendienteId);
        try {
            await axios.post(`${HOST_API}api/pagosUF/AsignarUnidadFuncionalPendiente`, {
                pagoImportadoPendienteId: pendiente.pagoImportadoPendienteId,
                unidadFuncionalId,
                administracionId,
                usuarioId,
            });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Pago imputado correctamente', life: 2500 });
            fetchPendientes();
            fetchPagos();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo imputar el pago', life: 4000 });
        } finally {
            setResolviendoId(null);
        }
    };

    const descartarPendiente = (pendiente) => {
        confirmDialog({
            message: `¿Descartar el movimiento de $ ${Number(pendiente.importe).toLocaleString('es-AR')} (${pendiente.banco})? Esta acción no se puede deshacer.`,
            header: 'Confirmar descarte',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Descartar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/pagosUF/DescartarPendiente`, {
                        params: { pagoImportadoPendienteId: pendiente.pagoImportadoPendienteId, administracionId, usuarioId },
                    });
                    fetchPendientes();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo descartar el movimiento', life: 3000 });
                }
            },
        });
    };

    // Descarga una plantilla Excel mínima (CSV válido) para que el usuario sepa el formato
    const descargarPlantilla = () => {
        const csv = 'UnidadFuncional;FechaPago;Importe;Detalle\n1;01/07/2026;15000;Pago expensa julio\n2;01/07/2026;12500;\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = 'plantilla_pagos.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─── Templates de columna ─────────────────────────────────────────────────────
    const importeTemplate  = (row) => fmtImporte(row.importe);
    const fechaTemplate    = (row) => fmtFecha(row.fechaPago);
    const periodoTemplate  = (row) => fmtPeriodo(row.periodo);
    const ESTADO_COLORES = {
        Pendiente: { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
        Aprobado:  { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
        Rechazado: { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
    };
    const estadoTemplate = (row) => {
        const s = ESTADO_COLORES[row.estado] || { bg: '#f2f4f7', color: '#344054', border: '#eaecf0' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {row.estado}
            </span>
        );
    };
    const accionesTemplate = (row) => (
        <div className="flex gap-1 justify-content-center">
            {row.estado === 'Pendiente' && (
                <>
                    <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text p-button-sm"
                        tooltip="Aprobar" tooltipOptions={{ position: 'top' }}
                        onClick={() => aprobarPago(row)} />
                    <Button icon="pi pi-times" className="p-button-rounded p-button-warning p-button-text p-button-sm"
                        tooltip="Rechazar" tooltipOptions={{ position: 'top' }}
                        onClick={() => rechazarPago(row)} />
                </>
            )}
            <Button icon="pi pi-ban" className="p-button-rounded p-button-danger p-button-text p-button-sm"
                tooltip="Anular" tooltipOptions={{ position: 'top' }}
                onClick={() => anularPago(row)} />
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Pagos</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            {/* ─── Barra superior ──────────────────────────────────────────────── */}
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap align-items-end">
                    <div>
                        <label style={lbl}>{t.UF}</label>
                        <Dropdown value={filtroUF} options={ufs} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setFiltroUF(e.value)} placeholder="Todas" showClear filter
                            className="p-inputtext-sm" style={{ width: 160 }} />
                    </div>
                    <div>
                        <label style={lbl}>Período</label>
                        <Calendar value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.value)}
                            view="month" dateFormat="mm/yy" placeholder="Todos" showButtonBar
                            className="p-inputtext-sm" style={{ width: 140 }} />
                    </div>
                    <Button label="Buscar" icon="pi pi-search" onClick={fetchPagos} className="p-button-sm" />
                </div>
                <div className="flex gap-2">
                    {pendientes.length > 0 && (
                        <Button label={`Pendientes de imputar (${pendientes.length})`} icon="pi pi-exclamation-triangle"
                            className="p-button-sm p-button-warning p-button-outlined"
                            onClick={() => setPendientesVisible(true)} />
                    )}
                    <Button label="Importar Extracto" icon="pi pi-file-excel" className="p-button-sm p-button-success"
                        onClick={() => { setModalExcel(true); setExcelFile(null); setExcelPeriodo(null); setExcelBanco(null); }} />
                    <Button label="Registrar Pago" icon="pi pi-plus" className="p-button-sm"
                        onClick={() => { setForm({ ...PAGO_EMPTY }); setArchivo(null); setModalPago(true); }} />
                </div>
            </div>

            {/* ─── Grilla ──────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable value={pagos} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay pagos registrados"
                        className="p-datatable-gridlines bg-white"
                        sortField="fechaPago" sortOrder={-1}>
                        <Column field="unidadFuncional"  header={t.UF}          sortable style={{ width: '5rem', textAlign: 'center' }} />
                        <Column field="persona"          header="Persona"      sortable />
                        <Column field="razonSocial"      header={t.Barrio}       sortable style={{ width: '10rem' }} />
                        <Column field="periodo"          header="Período"      sortable body={periodoTemplate} style={{ width: '9rem' }} />
                        <Column field="fechaPago"        header="Fecha pago"   sortable body={fechaTemplate}   style={{ width: '8rem' }} />
                        <Column field="importe"          header="Importe"      sortable body={importeTemplate} style={{ width: '9rem', textAlign: 'right' }} />
                        <Column field="formaPago"        header="Forma pago"   sortable style={{ width: '9rem' }} />
                        <Column field="detalle"          header="Detalle"      body={(r) => r.detalle || '—'} />
                        <Column field="usuario"          header="Registrado por" sortable style={{ width: '10rem' }} />
                        <Column header="Estado" body={estadoTemplate} style={{ width: '7rem' }} />
                        <Column header="Acciones" body={accionesTemplate} style={{ width: '10rem', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: Registrar Pago ────────────────────────────────────────── */}
            <Dialog header="Registrar Pago" visible={modalPago} style={{ width: '480px' }} modal
                onHide={() => setModalPago(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalPago(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarPago} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>{t.UnidadFuncional} *</label>
                        <Dropdown value={form.unidadFuncionalId} options={ufs} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setForm(f => ({ ...f, unidadFuncionalId: e.value }))}
                            placeholder={`Seleccionar ${t.UF}`} filter className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Período *</label>
                        <Calendar value={form.periodo} onChange={(e) => setForm(f => ({ ...f, periodo: e.value }))}
                            view="month" dateFormat="mm/yy" placeholder="mm/aaaa" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Fecha de pago *</label>
                        <Calendar value={form.fechaPago} onChange={(e) => setForm(f => ({ ...f, fechaPago: e.value }))}
                            dateFormat="dd/mm/yy" placeholder="dd/mm/aaaa" className="p-inputtext-sm" showIcon />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Forma de pago *</label>
                        <Dropdown value={form.formaPagoId} options={FORMAS_PAGO}
                            onChange={(e) => setForm(f => ({ ...f, formaPagoId: e.value }))}
                            placeholder="Seleccionar" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Concepto</label>
                        <Dropdown value={form.conceptoPagoId} options={CONCEPTOS}
                            onChange={(e) => setForm(f => ({ ...f, conceptoPagoId: e.value, planPagoCuotaId: null }))}
                            className="p-inputtext-sm" />
                    </div>

                    {/* ─── Expensa: vencimiento 1 / 2 / otros ─────────────────────── */}
                    {form.conceptoPagoId === 1 && (
                        <div className="col-12">
                            <label style={lbl}>Vencimiento a abonar *</label>
                            {loadingInfoExpensa ? (
                                <div className="flex align-items-center gap-2" style={{ color: '#667085', fontSize: '0.85rem' }}>
                                    <i className="pi pi-spin pi-spinner" /> Buscando información de la expensa...
                                </div>
                            ) : (
                                <div className="flex flex-column gap-2">
                                    <div className="flex align-items-center gap-2">
                                        <RadioButton
                                            inputId="venc1"
                                            name="vencimiento"
                                            value="v1"
                                            checked={ventanaPago === 'v1'}
                                            disabled={!infoExpensa}
                                            onChange={() => seleccionarVencimiento('v1')}
                                        />
                                        <label htmlFor="venc1" style={{ fontSize: '0.87rem' }}>
                                            Vencimiento 1{infoExpensa ? ` · ${fmtImporte(infoExpensa.importePrimerVencimiento)}` : ''}
                                        </label>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <RadioButton
                                            inputId="venc2"
                                            name="vencimiento"
                                            value="v2"
                                            checked={ventanaPago === 'v2'}
                                            disabled={!infoExpensa}
                                            onChange={() => seleccionarVencimiento('v2')}
                                        />
                                        <label htmlFor="venc2" style={{ fontSize: '0.87rem' }}>
                                            Vencimiento 2{infoExpensa ? ` · ${fmtImporte(infoExpensa.importeSegundoVencimiento)}` : ''}
                                        </label>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <RadioButton
                                            inputId="vencOtros"
                                            name="vencimiento"
                                            value="otros"
                                            checked={ventanaPago === 'otros'}
                                            onChange={() => seleccionarVencimiento('otros')}
                                        />
                                        <label htmlFor="vencOtros" style={{ fontSize: '0.87rem' }}>Otros</label>
                                    </div>
                                </div>
                            )}
                            {ventanaPago === 'otros' && (
                                <InputNumber
                                    value={form.importe}
                                    onValueChange={(e) => setForm(f => ({ ...f, importe: e.value }))}
                                    mode="currency" currency="ARS" locale="es-AR"
                                    placeholder="Importe"
                                    className="p-inputtext-sm mt-2"
                                />
                            )}
                        </div>
                    )}

                    {form.conceptoPagoId === 2 && (
                        <>
                            <div className="col-12">
                                <label style={lbl}>Plan de pago *</label>
                                <Dropdown value={planPagoId} options={planes}
                                    optionLabel="planPagoId" optionValue="planPagoId"
                                    itemTemplate={planLabel}
                                    valueTemplate={(option) => option ? planLabel(option) : 'Seleccionar plan'}
                                    onChange={(e) => seleccionarPlan(e.value)}
                                    placeholder={!form.unidadFuncionalId ? 'Primero seleccioná la UF' : 'Seleccionar plan'}
                                    disabled={!form.unidadFuncionalId}
                                    loading={loadingPlanes}
                                    emptyMessage="Esta UF no tiene planes de pago con saldo"
                                    className="p-inputtext-sm" />
                            </div>
                            <div className="col-12">
                                <label style={lbl}>Cuota *</label>
                                <Dropdown value={form.planPagoCuotaId} options={cuotas}
                                    optionLabel="planPagoCuotaId" optionValue="planPagoCuotaId"
                                    itemTemplate={cuotaLabel}
                                    valueTemplate={(option) => option ? cuotaLabel(option) : 'Seleccionar cuota'}
                                    onChange={(e) => seleccionarCuota(e.value)}
                                    placeholder={!planPagoId ? 'Primero seleccioná el plan' : 'Seleccionar cuota'}
                                    disabled={!planPagoId}
                                    loading={loadingCuotas}
                                    emptyMessage="Este plan no tiene cuotas pendientes"
                                    className="p-inputtext-sm" />
                            </div>
                        </>
                    )}
                    <div className="col-12">
                        <label style={lbl}>Detalle</label>
                        <InputText value={form.detalle} onChange={(e) => setForm(f => ({ ...f, detalle: e.target.value }))}
                            placeholder="Observaciones opcionales" className="p-inputtext-sm" />
                    </div>

                    {/* Comprobante adjunto (opcional) */}
                    <div className="col-12">
                        <label style={lbl}>
                            Comprobante&nbsp;
                            <span style={{ fontWeight: 400, color: '#98a2b3' }}>(opcional)</span>
                        </label>

                        {!archivo ? (
                            <div
                                onClick={() => comprobanteInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleDrop}
                                style={{
                                    border:       `2px dashed ${dragOver ? '#1570ef' : '#d0d5dd'}`,
                                    borderRadius: 10,
                                    padding:      '1.25rem 1rem',
                                    textAlign:    'center',
                                    cursor:       'pointer',
                                    background:   dragOver ? '#f0f6ff' : '#fafafa',
                                    transition:   'all 0.15s',
                                }}
                            >
                                <i className="pi pi-cloud-upload" style={{ fontSize: '1.6rem', color: dragOver ? '#1570ef' : '#98a2b3', marginBottom: '0.4rem', display: 'block' }} />
                                <p style={{ margin: 0, fontWeight: 500, color: '#344054', fontSize: '0.85rem' }}>
                                    Arrastrá o <span style={{ color: '#1570ef' }}>seleccioná</span> un archivo
                                </p>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#98a2b3' }}>
                                    PDF, JPG, PNG — máx. 10 MB
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '0.75rem',
                                border: '1px solid #d1fadf', borderRadius: 8,
                                padding: '0.7rem 1rem', background: '#f6fef9',
                            }}>
                                <i className="pi pi-file" style={{ fontSize: '1.3rem', color: '#12b76a' }} />
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    <p style={{ margin: 0, fontWeight: 600, color: '#101828', fontSize: '0.83rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {archivo.name}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#667085' }}>{fmtSize(archivo.size)}</p>
                                </div>
                                <button type="button" onClick={handleRemoveFile}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#98a2b3' }}>
                                    <i className="pi pi-times" />
                                </button>
                            </div>
                        )}

                        <input
                            ref={comprobanteInputRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            onChange={(e) => setFile(e.target.files[0])}
                        />
                    </div>
                </div>
            </Dialog>

            {/* ─── Modal: Importar Excel ────────────────────────────────────────── */}
            <Dialog header="Importar Pagos desde Excel" visible={modalExcel} style={{ width: '460px' }} modal
                onHide={() => setModalExcel(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalExcel(false)} disabled={importing} />
                        <Button label="Importar" icon="pi pi-upload" className="p-button-sm p-button-success" onClick={importarExcel} loading={importing} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    {/* Formato esperado */}
                    {/* <div className="col-12">
                        <div style={{ background: '#f8f9fa', border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#344054' }}>
                            <strong>Formato del archivo:</strong><br />
                            <span style={{ color: '#667085' }}>Columnas (sin encabezado de datos, fila 1 = títulos):</span>
                            <ol style={{ margin: '0.35rem 0 0 1rem', padding: 0, lineHeight: 1.8 }}>
                                <li><b>A:</b> {t.UnidadFuncional}</li>
                                <li><b>B:</b> Fecha de pago (dd/mm/aaaa)</li>
                                <li><b>C:</b> Importe (ej: 15000.00)</li>
                                <li><b>D:</b> Detalle (opcional)</li>
                            </ol>
                        </div>
                        <Button label="Descargar plantilla" icon="pi pi-download" className="p-button-text p-button-sm mt-2"
                            onClick={descargarPlantilla} />
                    </div> */}

                    <div className="col-12">
                        <label style={lbl}>Banco *</label>
                        <Dropdown value={excelBanco} options={BANCOS}
                            onChange={(e) => { setExcelBanco(e.value); setExcelFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                            placeholder="Seleccione el banco" className="p-inputtext-sm" />
                        {excelBanco && (
                            <small style={{ color: '#667085' }}>
                                Formato esperado: .{EXTENSIONES_POR_BANCO[excelBanco].join(' o .')}
                            </small>
                        )}
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Período *</label>
                        <Calendar value={excelPeriodo} onChange={(e) => setExcelPeriodo(e.value)}
                            view="month" dateFormat="mm/yy" placeholder="mm/aaaa" className="p-inputtext-sm" />
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Archivo *</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input ref={fileInputRef} type="file"
                                accept={(excelBanco ? EXTENSIONES_POR_BANCO[excelBanco] : ['xlsx', 'xls', 'csv']).map(e => `.${e}`).join(',')}
                                style={{ display: 'none' }}
                                onChange={handleExcelChange} />
                            <Button label={excelFile ? excelFile.name : 'Seleccionar archivo'}
                                icon="pi pi-file-excel"
                                className={`p-button-outlined p-button-sm ${excelFile ? 'p-button-success' : ''}`}
                                style={{ flex: 1, justifyContent: 'flex-start', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                onClick={() => fileInputRef.current.click()} />
                            {excelFile && (
                                <Button icon="pi pi-times" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                    onClick={() => { setExcelFile(null); fileInputRef.current.value = ''; }} />
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* ─── Modal: Pendientes de imputar ─────────────────────────────────── */}
            <Dialog header="Pendientes de imputar" visible={pendientesVisible} style={{ width: '900px' }} modal
                onHide={() => setPendientesVisible(false)}>
                <p style={{ color: '#667085', marginTop: 0 }}>
                    Movimientos importados donde no se pudo determinar automáticamente la {t.UF} (no se encontró
                    o hay más de una persona con ese DNI/CUIT). Asignala a mano o descartá el movimiento si no corresponde.
                </p>
                <DataTable value={pendientes} loading={loadingPendientes} paginator rows={10}
                    showGridlines responsiveLayout="scroll" emptyMessage="No hay movimientos pendientes de imputar"
                    className="p-datatable-gridlines bg-white" sortField="fechaPago" sortOrder={-1}>
                    <Column field="banco" header="Banco" sortable style={{ width: '7rem' }} />
                    <Column field="fechaPago" header="Fecha" sortable body={(r) => fmtFecha(r.fechaPago)} style={{ width: '7rem' }} />
                    <Column field="importe" header="Importe" sortable body={(r) => fmtImporte(r.importe)} style={{ width: '9rem', textAlign: 'right' }} />
                    <Column header="Detectado" body={(r) => r.nombreDetectado || r.documentoDetectado || '—'} style={{ width: '9rem' }} />
                    <Column field="detalle" header="Detalle" body={(r) => r.detalle || '—'} />
                    <Column header={t.UF} body={(r) => (
                        <Dropdown value={ufPorPendiente[r.pagoImportadoPendienteId] || null} options={ufs}
                            optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setUfPorPendiente(p => ({ ...p, [r.pagoImportadoPendienteId]: e.value }))}
                            placeholder={`Elegir ${t.UF}`} filter className="p-inputtext-sm" style={{ minWidth: 140 }} />
                    )} style={{ width: '13rem' }} />
                    <Column header="Acciones" body={(r) => (
                        <div className="flex gap-1 justify-content-center">
                            <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text p-button-sm"
                                tooltip="Imputar a la UF elegida" tooltipOptions={{ position: 'top' }}
                                loading={resolviendoId === r.pagoImportadoPendienteId}
                                onClick={() => asignarUnidadFuncional(r)} />
                            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text p-button-sm"
                                tooltip="Descartar" tooltipOptions={{ position: 'top' }}
                                onClick={() => descartarPendiente(r)} />
                        </div>
                    )} style={{ width: '7rem', textAlign: 'center' }} />
                </DataTable>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default FormularioPago;
